import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Button,
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Popover,
} from 'react-aria-components';
import { parseDate, getLocalTimeZone, DateFormatter, today } from '@internationalized/date';
import { useTheme } from "@mui/material/styles";

import { I18nProvider } from 'react-aria';
import './DatePickerDob.css';

const handleResizeObserverError = (error) => {
  if (error.message.includes('ResizeObserver loop completed with undelivered notifications')) return;
  console.error('ResizeObserver error:', error);
};

if (typeof window !== 'undefined') {
  window.addEventListener('error', handleResizeObserverError);
}

const getBlockStart = (year) => {
  const baseStart = 1926; 
  const blockSize = 12;
  return baseStart + Math.floor((year - baseStart) / blockSize) * blockSize;
};

const checkAgeLimit = (dateValue) => {
  if (!dateValue) return 'Date is required';
  const birthDate = new Date(dateValue.year, dateValue.month - 1, dateValue.day);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  if (age < 16) return 'Customer must be at least 16 years old';
  if (age > 120) return 'Customer must not above 120 years old';
  return '';
};

const DatePickerField = ({
  fieldName,
  formData,
  handleDateChange,
  handleBlur = () => {},
  touchedFields,
  fieldErrors,
}) => {
  const parseInitialDate = useCallback((dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;
    const parts = dateString.split('-');
    if (parts.length === 3) {
      const [mm, dd, yyyy] = parts;
      if (yyyy.length === 4) {
        try {
          return parseDate(`${yyyy}-${mm}-${dd}`);
        } catch {
          return null;
        }
      }
    }
    return null;
  }, []);
  const cellRefs = useRef({});
  const [isOpen, setIsOpen] = useState(false)
  const theme = useTheme();
  const [dateValue, setDateValue] = useState(() => parseInitialDate(formData[fieldName]));
  const defaultDate = dateValue || today(getLocalTimeZone());
  const [visibleYear, setVisibleYear] = useState(defaultDate.year);
  const [visibleMonth, setVisibleMonth] = useState(defaultDate.month);
  const [focusedDate, setFocusedDate] = useState(defaultDate);
  const [updateTimeout, setUpdateTimeout] = useState(null);
  const [mode, setMode] = useState('day');
  const pendingYearFocusIndexRef = useRef(null);
  const calendarRef = useRef(null);

  const focusNextCell = (currentDate) => {
    const nextDate = currentDate.add({ days: 1 });
    const nextCellKey = `${nextDate.year}-${nextDate.month}-${nextDate.day}`;
    if (cellRefs.current[nextCellKey]) {
      cellRefs.current[nextCellKey].focus();
    }
  };

  useEffect(() => {
    if (dateValue) {
      setVisibleYear(dateValue.year);
      setVisibleMonth(dateValue.month);
      setFocusedDate(dateValue);
    }
  }, [dateValue]);

  useEffect(() => {
    const parsed = parseInitialDate(formData[fieldName]);
    if (parsed?.toString() !== dateValue?.toString()) {
      if (updateTimeout) clearTimeout(updateTimeout);
      const timeout = setTimeout(() => {
        setDateValue(parsed || null);
        if (parsed) {
          setVisibleYear(parsed.year);
          setVisibleMonth(parsed.month);
          setFocusedDate(parsed);
        }
      }, 10);
      setUpdateTimeout(timeout);
    }
    return () => {
      if (updateTimeout) clearTimeout(updateTimeout);
    };
  }, [formData[fieldName], parseInitialDate]);

  const formatDateForForm = useCallback((dateObj) => {
    const formatter = new DateFormatter('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      calendar: 'gregory',
    });
    return formatter.format(dateObj.toDate(getLocalTimeZone())).replace(/\//g, '-');
  }, []);

  const isInvalidFormat = useCallback((val) => {
    const parts = val?.split('-') || [];
    const [mm, dd, yyyy] = parts;
    return parts.length !== 3 || mm.length !== 2 || dd.length !== 2 || yyyy.length !== 4;
  }, []);

  const isDateInRange = useCallback((date) => {
    const min = parseDate('1926-01-01');
    const max = parseDate('2026-12-31');
    return date.compare(min) >= 0 && date.compare(max) <= 0;
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (mode !== 'day') return;

    const { key } = event;
    let newFocusedDate = focusedDate;

    switch (key) {
      case 'ArrowLeft':
        event.preventDefault();
        newFocusedDate = focusedDate.subtract({ days: 1 });
        break;
      case 'ArrowRight':
        event.preventDefault();
        newFocusedDate = focusedDate.add({ days: 1 });
        break;
      case 'ArrowUp':
        event.preventDefault();
        newFocusedDate = focusedDate.subtract({ weeks: 1 });
        break;
      case 'ArrowDown':
        event.preventDefault();
        newFocusedDate = focusedDate.add({ weeks: 1 });
        break;
      case 'Home':
        event.preventDefault();
        newFocusedDate = focusedDate.set({ day: 1 });
        break;
      case 'End':
        event.preventDefault();
        const lastDay = focusedDate.add({ months: 1 }).subtract({ days: 1 }).day;
        newFocusedDate = focusedDate.set({ day: lastDay });
        break;
      case 'PageUp':
        event.preventDefault();
        if (event.shiftKey) {
        
          newFocusedDate = focusedDate.subtract({ years: 1 });
        } else {
         
          newFocusedDate = focusedDate.subtract({ months: 1 });
        }
        break;
      case 'PageDown':
        event.preventDefault();
        if (event.shiftKey) {
         
          newFocusedDate = focusedDate.add({ years: 1 });
        } else {
        
          newFocusedDate = focusedDate.add({ months: 1 });
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (isDateInRange(focusedDate)) {
          handleDateSelect(focusedDate);
        }
        return;
      default:
        return;
    }

    if (isDateInRange(newFocusedDate)) {
      setFocusedDate(newFocusedDate);

      if (newFocusedDate.month !== visibleMonth || newFocusedDate.year !== visibleYear) {
        setVisibleMonth(newFocusedDate.month);
        setVisibleYear(newFocusedDate.year);
      }
    }
  }, [mode, focusedDate, visibleMonth, visibleYear, isDateInRange]);

  useEffect(() => {
    const handleGlobalKeyDown = (event) => {
      if (calendarRef.current && calendarRef.current.contains(document.activeElement)) {
        handleKeyDown(event);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [handleKeyDown]);

  const handleConfirm = useCallback(() => {
    if (!dateValue) {
      handleDateChange(fieldName, '');
      return;
    }

    const formatted = formatDateForForm(dateValue);

    if (!/^\d{2}-\d{2}-\d{4}$/.test(formatted)) {
      handleDateChange(fieldName, '');
      handleBlur(fieldName, 'MM-DD-YYYY is required');
      
      const t = today(getLocalTimeZone());
      setVisibleYear(t.year);
      setVisibleMonth(t.month);
      setFocusedDate(t);
      setDateValue(null);
      return;
    }

    if (
      fieldName.toLowerCase().includes('dateofbirth') ||
      fieldName.toLowerCase().includes('newAgevariable')
    ) {
      const ageError = checkAgeLimit(dateValue);
      if (ageError) {
        handleDateChange(fieldName, '');
        handleBlur(fieldName, ageError);
       
        const t = today(getLocalTimeZone());
        setVisibleYear(t.year);
        setVisibleMonth(t.month);
        setFocusedDate(t);
        setDateValue(null);
        return;
      }
    }

    if (!isDateInRange(dateValue)) {
      handleDateChange(fieldName, '');
      handleBlur(fieldName, 'Allowed range (1926 - 2026)');
    
      const t = today(getLocalTimeZone());
      setVisibleYear(t.year);
      setVisibleMonth(t.month);
      setFocusedDate(t);
      setDateValue(null);
      return;
    }

    handleDateChange(fieldName, formatted);
    handleBlur(fieldName, '');
  }, [dateValue, fieldName, handleBlur, handleDateChange]);



  useEffect(() => {
    if (dateValue) {
      const formatted = formatDateForForm(dateValue);
      const ageError = checkAgeLimit(dateValue); 
      const isValidFormat = !isInvalidFormat(formatted) && isDateInRange(dateValue);

      if (isValidFormat && !ageError && fieldErrors?.[fieldName]) {
        handleBlur(fieldName, '');
      }
    }
  }, [dateValue, fieldName, formatDateForForm, isInvalidFormat, isDateInRange, fieldErrors, handleBlur]);


  const handleDateSelect = useCallback((date) => {
    if (!isDateInRange(date)) {
      handleBlur(fieldName, 'Date is out of allowed range');
      return;
    }
    setDateValue(date);
    setFocusedDate(date);
    const formatted = formatDateForForm(date);
    handleDateChange(fieldName, formatted);
    handleBlur(fieldName, '');
  }, [isDateInRange, formatDateForForm, fieldName, handleDateChange, handleBlur]);

  const handlePrevNavigation = useCallback(() => {
    if (mode === 'year') {
      setVisibleYear(y => Math.max(1926, y - 12));
    } else if (mode === 'day') {
      if (visibleMonth === 1) {
        if (visibleYear > 1926) {
          setVisibleMonth(12);
          setVisibleYear(y => y - 1);
          const newFocused = focusedDate.set({ year: visibleYear - 1, month: 12 });
          setFocusedDate(newFocused);
          if (dateValue) {
            setDateValue(prev => prev.set({ year: prev.year - 1, month: 12 }));
          }
        }
      } else {
        setVisibleMonth(m => m - 1);
        const newFocused = focusedDate.set({ month: visibleMonth - 1 });
        setFocusedDate(newFocused);
        if (dateValue) {
          setDateValue(prev => prev.set({ month: prev.month - 1 }));
        }
      }
    } else if (mode === 'month') {
      setVisibleYear(y => Math.max(1926, y - 1));
    }
  }, [mode, visibleMonth, visibleYear, focusedDate, dateValue]);

  const handleNextNavigation = useCallback(() => {
    if (mode === 'year') {
      setVisibleYear(y => Math.min(2026, y + 12));
    } else if (mode === 'day') {
      if (visibleMonth === 12) {
        if (visibleYear < 2026) {
          setVisibleMonth(1);
          setVisibleYear(y => y + 1);
          const newFocused = focusedDate.set({ year: visibleYear + 1, month: 1 });
          setFocusedDate(newFocused);
          if (dateValue) {
            setDateValue(prev => prev.set({ year: prev.year + 1, month: 1 }));
          }
        }
      } else {
        setVisibleMonth(m => m + 1);
        const newFocused = focusedDate.set({ month: visibleMonth + 1 });
        setFocusedDate(newFocused);
        if (dateValue) {
          setDateValue(prev => prev.set({ month: prev.month + 1 }));
        }
      }
    } else if (mode === 'month') {
      setVisibleYear(y => Math.min(2026, y + 1));
    }
  }, [mode, visibleMonth, visibleYear, focusedDate, dateValue]);

  const renderYearGrid = useCallback(() => {
    const baseStart = 1926;
    const blockSize = 12;
    const offset = visibleYear - baseStart;
    const blockIndex = Math.floor(offset / blockSize);
    const start = baseStart + blockSize * blockIndex;
    const totalCells = blockSize;
    const rowSize = 3;

    const handleYearKeyDown = (e, index) => {
      let newIndex = index;
      const minYear = 1926;
      const maxYear = 2026;
      const allowedBlockStart = getBlockStart(minYear);
      const allowedBlockEnd = getBlockStart(maxYear);

      switch (e.key) {
        case 'ArrowLeft':
          if (index % rowSize === 0) {
            const newStart = start - totalCells;
            if (newStart >= allowedBlockStart) {
              pendingYearFocusIndexRef.current = index + rowSize - 1;
              setVisibleYear(newStart);
            }
            return;
          } else {
            newIndex = index - 1;
          }
          break;

        case 'ArrowRight':
          if (index % rowSize === rowSize - 1) {
            const newStart = start + totalCells;
            if (start + totalCells <= allowedBlockEnd) {
              pendingYearFocusIndexRef.current = index - rowSize + 1;
              setVisibleYear(newStart);
            }
            return;
          } else {
            newIndex = index + 1;
          }
          break;

        case 'ArrowUp':
          if (index < rowSize) {
            const newStart = start - totalCells;
            if (newStart >= allowedBlockStart) {
              pendingYearFocusIndexRef.current = index + totalCells - rowSize;
              setVisibleYear(newStart);
            }
            return;
          } else {
            newIndex = index - rowSize;
          }
          break;

        case 'ArrowDown':
          if (index + rowSize >= totalCells) {
            const newStart = start + totalCells;
            if (start + totalCells <= allowedBlockEnd) {
              pendingYearFocusIndexRef.current = index % rowSize;
              setVisibleYear(newStart);
            }
            return;
          } else {
            newIndex = index + rowSize;
          }
          break;

        case 'Enter':
        case 'Enter':
        case ' ':
          e.preventDefault();
          const selectedYear = start + index;
          if (selectedYear >= minYear && selectedYear <= maxYear) {
            setVisibleYear(selectedYear);
            if (dateValue) {
              const newDate = dateValue.set({ year: selectedYear });
              setDateValue(newDate);
              setFocusedDate(newDate);
              setMode('day');
              setTimeout(() => {
                const targetCell = document.querySelector(`[data-date="${newDate.toString()}"]`);
                if (targetCell) targetCell.focus();
              }, 0);
            } else {
              setMode('month');
              setTimeout(() => {
                document.querySelectorAll('.month-cell')[0]?.focus();
              }, 0);
            }
          }
          return;
        default:
          return;
      }

      e.preventDefault();

      const targetYear = start + newIndex;
      if (targetYear >= minYear && targetYear <= maxYear && newIndex >= 0 && newIndex < totalCells) {
        const allCells = document.querySelectorAll('.year-cell');
        if (allCells[newIndex]) {
          allCells[newIndex].focus();
        }
      }
    };

    return (
      <div className="year-grid">
        {Array.from({ length: blockSize }, (_, index) => {
          const year = start + index;
          return (
            <div
              key={year}
              className={`year-cell ${year === dateValue?.year ? 'yearHighlighted' : ''}`}
              tabIndex={0}
              onClick={() => {
                setVisibleYear(year);
                if (dateValue) {
                  setDateValue(prev => prev.set({ year }));
                  setMode('day');
                  setTimeout(() => {
                    const targetCell = document.querySelector(`[data-date="${focusedDate.toString()}"]`);
                    if (targetCell) targetCell.focus();
                  }, 0);
                } else {
                  setMode('month');
                  setTimeout(() => {
                    document.querySelectorAll('.month-cell')[0]?.focus();
                  }, 0);
                }
              }}
              onKeyDown={(e) => handleYearKeyDown(e, index)}
            >
              {year}
            </div>
          );
        })}
      </div>
    );
  }, [visibleYear, dateValue]);

  useEffect(() => {
    if (pendingYearFocusIndexRef.current !== null) {
      const index = pendingYearFocusIndexRef.current;
      const allCells = document.querySelectorAll('.year-cell');
      if (allCells[index]) {
        allCells[index].focus();
      }
      pendingYearFocusIndexRef.current = null;
    }
  }, [visibleYear]);

  const renderMonthGrid = useCallback(() => {
    const months = Array.from({ length: 12 }, (_, i) =>
      new Date(0, i).toLocaleString('en-US', { month: 'short' })
    );

    const handleMonthKeyDown = (e, index) => {
      const rowSize = 3;
      const totalCells = 12;
      let newIndex = index;

      switch (e.key) {
        case 'ArrowLeft':
          newIndex = index - 1;
          break;
        case 'ArrowRight':
          newIndex = index + 1;
          break;
        case 'ArrowUp':
          newIndex = index - rowSize;
          break;
        case 'ArrowDown':
          newIndex = index + rowSize;
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          const selectedMonth = index + 1;
          const newDate = dateValue
            ? dateValue.set({ year: visibleYear, month: selectedMonth })
            : parseDate(`${visibleYear}-${String(selectedMonth).padStart(2, '0')}-01`);
          setVisibleMonth(selectedMonth);
          setDateValue(newDate);
          setFocusedDate(newDate);
          setMode('day');
          setTimeout(() => {
            const targetCell = document.querySelector(`[data-date="${focusedDate.toString()}"]`);
            if (targetCell) targetCell.focus();
          }, 0);
          return;
        default:
          return;
      }

      e.preventDefault();

      if (newIndex < 0) {
        setVisibleYear(prev => {
          const newYear = Math.max(1926, prev - 1);
          setTimeout(() => {
            const allCells = document.querySelectorAll('.month-cell');
            if (allCells[totalCells - 1]) {
              allCells[totalCells - 1].focus(); 
            }
          }, 0);
          return newYear;
        });
      } else if (newIndex >= totalCells) {
        setVisibleYear(prev => {
          const newYear = Math.min(2026, prev + 1);
          setTimeout(() => {
            const allCells = document.querySelectorAll('.month-cell');
            if (allCells[0]) {
              allCells[0].focus(); 
            }
          }, 0);
          return newYear;
        });
      } else {
        const allCells = document.querySelectorAll('.month-cell');
        if (allCells[newIndex]) {
          allCells[newIndex].focus();
        }
      }
    };

    return (
      <div className="month-grid">
        {months.map((label, index) => (
          <div
            key={index}
            className={`month-cell ${index + 1 === dateValue?.month && visibleYear === dateValue?.year ? 'monthHighlighted' : ''}`}
            tabIndex={0}
            onClick={() => {
              const selectedMonth = index + 1;
              const newDate = dateValue
                ? dateValue.set({ year: visibleYear, month: selectedMonth })
                : parseDate(`${visibleYear}-${String(selectedMonth).padStart(2, '0')}-01`);
              setVisibleMonth(selectedMonth);
              setDateValue(newDate);
              setFocusedDate(newDate);
              setMode('day');
              setTimeout(() => {
                const targetCell = document.querySelector(`[data-date="${focusedDate.toString()}"]`);
                if (targetCell) targetCell.focus();
              }, 0);

            }}
            onKeyDown={(e) => handleMonthKeyDown(e, index)}
          >
            {label}
          </div>
        ))}
      </div>
    );
  }, [visibleMonth, visibleYear, dateValue]);

  const calendarTitle = useMemo(() => {
    if (mode === 'year') {
      const baseStart = 1926;
      const blockSize = 12;
      const offset = visibleYear - baseStart;
      const blockIndex = Math.floor(offset / blockSize);
      const start = baseStart + blockIndex * blockSize;
      return `${start} - ${Math.min(2026, start + blockSize - 1)}`;
    } else {
      return (
        <span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => {
              setMode('month');
              const selectedIndex = dateValue?.month ? dateValue.month - 1 : 0;
              setTimeout(() => {
                document.querySelectorAll('.month-cell')[selectedIndex]?.focus();
              }, 0);

            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setMode('month');
                const selectedIndex = dateValue?.month ? dateValue.month - 1 : 0;
                setTimeout(() => {
                  document.querySelectorAll('.month-cell')[selectedIndex]?.focus();
                }, 0);

              }
            }}

            style={{ cursor: 'pointer', marginRight: '4px', textDecoration: 'underline' }}
          >
            {new Date(visibleYear, visibleMonth - 1).toLocaleString('en-US', { month: 'long' })}
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => {
              setMode('year');
              const baseStart = 1926;
              const blockSize = 12;
              const offset = dateValue?.year ? dateValue.year - baseStart : 0;
              const blockIndex = Math.floor(offset / blockSize);
              const start = baseStart + blockIndex * blockSize;
              const selectedYear = dateValue?.year || start;
              const selectedIndex = selectedYear - start;

              setTimeout(() => {
                document.querySelectorAll('.year-cell')[selectedIndex]?.focus();
              }, 0);

            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setMode('year');
                const baseStart = 1926;
                const blockSize = 12;
                const offset = dateValue?.year ? dateValue.year - baseStart : 0;
                const blockIndex = Math.floor(offset / blockSize);
                const start = baseStart + blockIndex * blockSize;
                const selectedYear = dateValue?.year || start;
                const selectedIndex = selectedYear - start;

                setTimeout(() => {
                  document.querySelectorAll('.year-cell')[selectedIndex]?.focus();
                }, 0);

              }
            }}

            style={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            {visibleYear}
          </span>
        </span>
      );
    }
  }, [mode, visibleYear, visibleMonth]);


  return (
    <I18nProvider locale="en-US">
      <div className="date-picker-container">
        <DatePicker
          aria-label="Select date"
          value={dateValue}
          shouldForceLeadingZeros
          onChange={setDateValue}
          className="react-aria-DatePicker"
          granularity="day"
          onBlur={handleConfirm}
          isOpen={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) {
              setMode('day');
              const fallbackDate = dateValue || today(getLocalTimeZone());
              setFocusedDate(fallbackDate);
              setVisibleYear(fallbackDate.year);
              setVisibleMonth(fallbackDate.month);
            } else {
              if (!dateValue) {
                const t = today(getLocalTimeZone());
                setFocusedDate(t);
                setVisibleYear(t.year);
                setVisibleMonth(t.month);
              }
            }
          }}
          id={fieldName}
          name={fieldName}
        >
          <Group className={`react-aria-Group ${touchedFields?.[fieldName] && fieldErrors?.[fieldName] ? 'error' : ''}`}>
            <DateInput className="react-aria-DateInput">
              {(segment) =>
                segment.type === 'literal' ? (
                  <span key={segment.key} className="separator">-</span>
                ) : (
                  <DateSegment
                    key={segment.key}
                    segment={segment}
                    className="react-aria-DateSegment"
                    id={`${fieldName}-${segment.type}`}
                    name={`${fieldName}-${segment.type}`}
                  />
                )
              }
            </DateInput>
            <Button className="react-aria-Button">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </Button>
          </Group>

          {touchedFields?.[fieldName] && fieldErrors?.[fieldName] && (
            <div className="date-picker-error-text">
              {fieldErrors[fieldName]}
            </div>
          )}

          <Popover className="react-aria-Popover">
            <Dialog className="react-aria-Dialog" ref={calendarRef}>
              <div className="calendar-header">
                <Button onPress={handlePrevNavigation}>◀</Button>
                <Heading className="calendar-title">
                  {calendarTitle}
                </Heading>
                <Button onPress={handleNextNavigation}>▶</Button>
              </div>

              {mode === 'year' && renderYearGrid()}
              {mode === 'month' && renderMonthGrid()}

              {mode === 'day' && (
                <Calendar
                  focusedValue={focusedDate}
                  onFocusChange={(newFocusedDate) => {
                    setFocusedDate(newFocusedDate);
                    setVisibleYear(newFocusedDate.year);
                    setVisibleMonth(newFocusedDate.month);
                  }}
                >
                  <CalendarGrid className="react-aria-CalendarGrid">
                    {(date) => {
                      const isSelected = dateValue?.toString() === date.toString();
                      const jsDate = new Date(date.year, date.month - 1, date.day);

                      return (
                        <CalendarCell
                          key={`${date.year}-${date.month}-${date.day}`}
                          date={date}
                          onPress={() => handleDateSelect(date)}
                          className={`react-aria-CalendarCell ${isSelected ? "selected" : ""}`}
                        >
                          {({ isFocused }) => (
                            <div
                              ref={(el) =>
                                (cellRefs.current[`${date.year}-${date.month}-${date.day}`] = el)
                              }
                              tabIndex={isFocused ? -1 : 0}
                              role="button"
                              aria-label={`Select ${jsDate.toDateString()}`}
                              onKeyDown={(e) => {
                                if (e.key === "Tab" && isSelected) {
                                  e.preventDefault();
                                }
                              }}
                              style={{
                                backgroundColor: isSelected
                                  ? theme.palette.primary.main       // ✅ selected = primary color
                                  : 'transparent',                   // ✅ normal = transparent
                                color: isSelected
                                  ? theme.palette.primary.contrastText
                                  : theme.palette.text.primary,
                                borderRadius: 6,
                                padding: '6px 10px',
                                cursor: 'pointer',
                                textAlign: 'center',
                                transition: 'all 0.2s ease',
                              }}

                              className={`calendar-cell ${
                                isFocused ? "focused" : ""
                              } ${
                                !dateValue &&
                                date.toString() === today(getLocalTimeZone()).toString()
                                  ? "todayHighlighted"
                                  : ""
                              }`}
                            >
                              {date.day}
                            </div>
                          )}
                        </CalendarCell>
                      );
                    }}
                  </CalendarGrid>
                </Calendar>
              )}
            </Dialog>
          </Popover>
        </DatePicker>
      </div>
    </I18nProvider>
  );
};

export default DatePickerField;